import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /**
   * SPF — sum of quantities optimistically added but not yet confirmed by the
   * server. While > 0 the bubble shows a pending spinner. Each confirming
   * product-form `cart:update` drains it; when it reaches 0 the spinner clears.
   * @type {number}
   */
  #pendingDelta = 0;

  /** @type {number | undefined} SPF — watchdog so the spinner can't hang. */
  #pendingWatchdog;

  /** @type {number} SPF — brief window after a resync where stale add events are ignored. */
  #suppressUntil = 0;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent || '0', 10) || 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    CartIcon.#injectPendingStyles();
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    if (this.#pendingWatchdog) clearTimeout(this.#pendingWatchdog);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      // A bfcache restore is a fresh slate — drop any in-flight optimistic state.
      this.#pendingDelta = 0;
      this.#clearPending();
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * SPF — instant optimistic feedback. Called synchronously the moment the
   * shopper taps "Add", *before* any network round-trip: the count ticks up and
   * a spinner appears on the bubble. The real `cart:update` that follows
   * confirms (drains #pendingDelta) or, on error/timeout, we resync the truth.
   *
   * This is the storefront's primary "it worked" signal — it must never depend
   * on the network or a free main thread to appear.
   * @param {number} quantity
   */
  beginOptimistic(quantity) {
    const qty = Number(quantity) || 1;
    this.#pendingDelta += qty;

    // Reveal the bubble and tick the number up immediately.
    this.refs.cartBubbleCount.classList.remove('hidden');
    this.refs.cartBubble.classList.remove('visually-hidden');
    this.currentCartCount = this.currentCartCount + qty;
    this.classList.add('header-actions__cart-icon--has-cart');
    this.classList.add('cart-icon--pending');

    // Backstop: if no confirming event arrives (lost request, jammed main
    // thread) reconcile to the server so the spinner can't hang forever.
    if (this.#pendingWatchdog) clearTimeout(this.#pendingWatchdog);
    this.#pendingWatchdog = setTimeout(this.#resyncFromServer, 7000);
  }

  #clearPending() {
    this.classList.remove('cart-icon--pending');
    if (this.#pendingWatchdog) {
      clearTimeout(this.#pendingWatchdog);
      this.#pendingWatchdog = undefined;
    }
  }

  /**
   * Pull the authoritative cart and snap the bubble to it. Off the critical
   * path — only used to recover from an error or a missed confirmation.
   */
  #resyncFromServer = async () => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      const count = cart.item_count ?? 0;

      this.#pendingDelta = 0;
      this.refs.cartBubbleCount.classList.toggle('hidden', count === 0);
      this.refs.cartBubble.classList.toggle('visually-hidden', count === 0);
      this.currentCartCount = count;
      this.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
      this.#persist();

      // Ignore any late add event that races in just after this resync —
      // the server count we just read already includes it.
      this.#suppressUntil = Date.now() + 1500;
    } catch (_) {
      // Leave the optimistic count in place; nothing better to show.
    }
    this.#clearPending();
  };

  #persist() {
    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );
  }

  /**
   * Handles the cart update event.
   * @param {CustomEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    // Confirm/settle an add we already showed optimistically.
    if (comingFromProductForm && this.#pendingDelta > 0) {
      if (this.#suppressUntil && Date.now() < this.#suppressUntil) return;

      if (event.detail.data?.didError) {
        // Backend state is uncertain (e.g. requested over the max) — snap to truth.
        this.#resyncFromServer();
        return;
      }

      this.#pendingDelta = Math.max(0, this.#pendingDelta - itemCount);
      this.#persist();

      if (this.#pendingDelta === 0) {
        this.#clearPending();
        this.#playBump();
      }
      return;
    }

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   * @param {boolean} [animate]
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    // An absolute (non-product-form) update is the source of truth, so drop any
    // stale optimistic state rather than letting it linger.
    if (!comingFromProductForm && this.#pendingDelta > 0) {
      this.#pendingDelta = 0;
      this.#clearPending();
    }

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    this.#persist();

    if (!animate || itemCount === 0) return;

    this.#playBump();
  };

  /**
   * Plays the cart bubble bounce. Uses a passive `animationend` listener (not
   * getAnimations()) to avoid forcing a synchronous full-document style recalc
   * on a large storefront — the old multi-second add-to-cart hang.
   */
  #playBump = async () => {
    // Ensure the element is visible before starting the animation.
    await new Promise((resolve) => requestAnimationFrame(resolve));

    const cartBubble = this.refs.cartBubble;
    cartBubble.classList.add('cart-bubble--animating');
    const stopAnimating = () => cartBubble.classList.remove('cart-bubble--animating');
    this.refs.cartBubbleText.addEventListener('animationend', stopAnimating, { once: true });
    setTimeout(stopAnimating, 1000);
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };

  /** @type {boolean} */
  static #stylesInjected = false;

  /**
   * SPF — one-time injection of the pending spinner ring around the cart bubble.
   * Kept in JS (next to the state it visualizes) so no extra Liquid edit is
   * needed. Only ever visible after a tap, so there's no flash-of-unstyled risk.
   */
  static #injectPendingStyles() {
    if (CartIcon.#stylesInjected) return;
    CartIcon.#stylesInjected = true;

    const css =
      '.header-actions__cart-icon.cart-icon--pending{position:relative;}' +
      '.header-actions__cart-icon.cart-icon--pending::after{content:"";position:absolute;' +
      'width:calc(var(--cart-bubble-size,20px) + 6px);height:calc(var(--cart-bubble-size,20px) + 6px);' +
      'top:calc(var(--cart-bubble-top,4.5px) - 3px);right:calc(var(--cart-bubble-right,2.5px) - 3px);' +
      'border-radius:50%;border:2px solid currentColor;border-right-color:transparent;' +
      'animation:spf-cart-spin .6s linear infinite;pointer-events:none;z-index:5;}' +
      '@keyframes spf-cart-spin{to{transform:rotate(360deg);}}' +
      '@media (prefers-reduced-motion:reduce){.header-actions__cart-icon.cart-icon--pending::after{animation-duration:1.4s;}}';

    const style = document.createElement('style');
    style.textContent = css;
    document.head.appendChild(style);
  }
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
